/**
 * Utility functions for Configurable Insights
 * Type guards, validators, and helper functions
 */

import {
  AmountFilter,
  AmountFieldFilterOperator,
  ConfigurableInsight,
  type EnrichmentValuesApiResponse,
  type EnrichmentValues,
  InsightCondition,
  InsightConditionType,
  TransactionConditionOperator,
} from '@/types/decision-engine/configurable-insights.types';
import {
  TRANSACTION_CATEGORY_LABELS,
  TRANSACTION_METHOD_LABELS,
} from '@/V2/decision-engine/configurable-insights/filter-constants';

// ============================================================================
// Comprehensive Validation with Error Messages
// ============================================================================

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Validate an entire insight configuration and return detailed error messages
 */
export function validateInsightConfiguration(
  insight: ConfigurableInsight,
): ValidationResult {
  const errors: string[] = [];

  if (!insight.signal_name || insight.signal_name.trim() === '') {
    errors.push('Insight name is required');
  }

  const { errors: configErrors } = validateInsightConditions(insight);
  errors.push(...configErrors);

  return { isValid: errors.length === 0, errors };
}

/**
 * Validate a single condition and return detailed error messages
 */
function validateConditionWithErrors(
  condition: InsightCondition,
  index: number,
): string[] {
  const errors: string[] = [];
  const conditionLabel = `Condition ${index + 1}`;

  // Check operator exists
  if (!condition.operator) {
    errors.push(`${conditionLabel}: Operator is required`);
    return errors;
  }

  // For transaction conditions, check operator_value requirements
  if (condition.condition_type === InsightConditionType.TRANSACTIONS) {
    if (condition.operator !== TransactionConditionOperator.ANY) {
      // GREATER_THAN and LESS_THAN require operator_value and operator_value_type
      if (!condition.operator_value_type) {
        errors.push(
          `${conditionLabel}: Operator value type is required for ${condition.operator} operator`,
        );
      }
      if (
        condition.operator_value === null ||
        condition.operator_value === undefined
      ) {
        errors.push(
          `${conditionLabel}: Operator value is required for ${condition.operator} operator`,
        );
      } else if (condition.operator_value <= 0) {
        errors.push(`${conditionLabel}: Operator value must be greater than 0`);
      }
    }
  }

  // Validate timeframe filter if present (in both transaction and balance conditions)
  if (condition.filters.timeframe) {
    if (!condition.filters.timeframe.mode) {
      errors.push(`${conditionLabel}: Timeframe mode is required`);
    }
  }

  // Validate base field filters if present (transaction conditions only)
  if (condition.condition_type === InsightConditionType.TRANSACTIONS) {
    const baseFieldFilters = [
      { key: 'category', label: 'Category' },
      { key: 'method', label: 'Method' },
      { key: 'merchant', label: 'Merchant' },
      { key: 'payment_processor', label: 'Payment Processor' },
    ] as const;

    for (const { key, label } of baseFieldFilters) {
      const filter = condition.filters[key];
      if (filter && (!filter.values || filter.values.length === 0)) {
        errors.push(
          `${conditionLabel}: ${label} filter requires at least one value`,
        );
      }
    }
  }

  // Validate amount filters if present
  if (condition.filters.amount) {
    const amountErrors = validateAmountFilterWithErrors(
      condition.filters.amount,
      `${conditionLabel}: Amount filter`,
    );
    errors.push(...amountErrors);
  }

  // For daily balance conditions, validate volatility filter
  if (
    condition.condition_type === InsightConditionType.DAILY_BALANCE &&
    condition.filters.volatility
  ) {
    const volatilityErrors = validateAmountFilterWithErrors(
      condition.filters.volatility,
      `${conditionLabel}: Volatility filter`,
    );
    errors.push(...volatilityErrors);
  }

  return errors;
}

/**
 * Validate an amount filter and return error messages
 */
function validateAmountFilterWithErrors(
  filter: AmountFilter,
  label: string,
): string[] {
  const errors: string[] = [];

  if (!filter.values || filter.values.length === 0) {
    errors.push(`${label}: At least one value is required`);
    return errors;
  }

  if (filter.operator === AmountFieldFilterOperator.BETWEEN) {
    if (filter.values.length !== 2) {
      errors.push(`${label}: BETWEEN operator requires exactly 2 values`);
    } else if (filter.values[0] >= filter.values[1]) {
      errors.push(
        `${label}: First value must be less than second value for BETWEEN operator`,
      );
    }
  } else {
    if (filter.values.length !== 1) {
      errors.push(`${label}: Operator requires exactly 1 value`);
    }
  }

  return errors;
}

/**
 * Validate insight configuration for the generate name/description flow.
 * Validates that conditions exist and are complete, but skips signal_name validation
 * since the name is what we're about to generate.
 */
export function validateInsightConditions(
  insight: ConfigurableInsight,
): ValidationResult {
  const errors: string[] = [];

  if (!insight.configuration) {
    errors.push('Configuration is required');
    return { isValid: false, errors };
  }

  if (
    !insight.configuration.conditions ||
    insight.configuration.conditions.length === 0
  ) {
    errors.push('At least one condition is required');
  } else {
    insight.configuration.conditions.forEach((condition, index) => {
      const conditionErrors = validateConditionWithErrors(condition, index);
      errors.push(...conditionErrors);
    });
  }

  return { isValid: errors.length === 0, errors };
}

/**
 * Remove null/undefined values from objects recursively
 * Used to clean data before sending to backend
 */
export function removeNullValues<T>(obj: T): T {
  if (Array.isArray(obj)) {
    return obj.map(removeNullValues) as T;
  }

  if (obj !== null && typeof obj === 'object') {
    const cleaned: Record<string, unknown> = {};
    for (const key in obj) {
      const value = (obj as Record<string, unknown>)[key];
      if (value !== null && value !== undefined) {
        cleaned[key] = removeNullValues(value);
      }
    }
    return cleaned as T;
  }

  return obj;
}

/**
 * Transform API enrichment values response to internal format
 * Uses label maps for customizable display text
 */
export function transformEnrichmentValues(
  apiResponse: EnrichmentValuesApiResponse,
): EnrichmentValues {
  return {
    transaction_categories: apiResponse.categories.map((value) => ({
      text: TRANSACTION_CATEGORY_LABELS[value] || value,
      value,
    })),
    transaction_methods: apiResponse.methods.map((value) => ({
      text: TRANSACTION_METHOD_LABELS[value] || value,
      value,
    })),
    payment_processors: (apiResponse.payment_processors || []).map((value) => ({
      text: value,
      value,
    })),
  };
}
